import { useEffect, useState } from 'react';
import { api, errorText, formatDate, formatDateTime, formatQty } from '../api';
import { Badge, Card, Empty, Loading, Notice, ReasonBadge } from '../components/ui';
import type { LotSummary, LotTrace } from '../types';

/**
 * Chargenrückverfolgung.
 *
 * Der Rückruffall: Charge eingeben, kompletten Weg sehen - woher sie kam, wo
 * sie noch liegt, in welchen Aufträgen sie das Haus verlassen hat und an
 * welche Kunden. Nichts wird verdichtet; im Ernstfall zählt jede Zeile.
 */
export function LotsPage() {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<LotSummary[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [trace, setTrace] = useState<LotTrace | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!term.trim()) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<LotSummary[]>('/api/stock/lots', { query: { q: term.trim() } })
        .then(setHits)
        .catch((err) => setError(errorText(err)));
    }, 220);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!openId) return;
    setTrace(null);
    api<LotTrace>(`/api/stock/lots/${openId}/trace`)
      .then(setTrace)
      .catch((err) => setError(errorText(err)));
  }, [openId]);

  const stillHere = trace?.stock.reduce((sum, row) => sum + row.qty, 0) ?? 0;
  const delivered = trace?.deliveries.reduce((sum, row) => sum + row.qty, 0) ?? 0;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Ablaufliste />

      <Card>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>Charge, Artikelnummer oder Bezeichnung</span>
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="z. B. CH-260415"
            style={{ fontSize: 16, padding: '10px 12px' }}
          />
        </label>
      </Card>

      {hits.length > 0 && !openId && (
        <Card title="Treffer" padded={false}>
          <table>
            <thead>
              <tr>
                <th>Charge</th>
                <th>Artikel</th>
                <th>MHD</th>
                <th className="num">Noch im Lager</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {hits.map((lot) => (
                <tr key={lot.id}>
                  <td className="mono">{lot.code}</td>
                  <td>
                    <span className="mono">{lot.sku}</span> {lot.article_name}
                  </td>
                  <td className="small">{formatDate(lot.best_before)}</td>
                  <td className="num">{formatQty(lot.qty)}</td>
                  <td className="num">
                    <button className="primary small" onClick={() => setOpenId(lot.id)}>
                      Weg verfolgen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {openId &&
        (!trace ? (
          <Loading />
        ) : (
          <>
            <Card
              title={`Charge ${trace.lot.code}`}
              actions={
                <button className="small" onClick={() => setOpenId(null)}>
                  zurück zur Suche
                </button>
              }
            >
              <div style={{ fontSize: 16 }}>
                <strong>{trace.lot.article_name}</strong>
              </div>
              <div className="small muted">
                <span className="mono">{trace.lot.sku}</span> · {trace.lot.owner_name}
                {trace.lot.best_before ? ` · MHD ${formatDate(trace.lot.best_before)}` : ''}
                {trace.lot.supplier_ref ? ` · Lieferantenreferenz ${trace.lot.supplier_ref}` : ''}
              </div>

              <div className="grid kpi" style={{ marginTop: 16 }}>
                <div className="kpi-tile">
                  <div className="label">Noch im Lager</div>
                  <div className="value">{formatQty(stillHere)}</div>
                </div>
                <div className="kpi-tile">
                  <div className="label">Ausgeliefert</div>
                  <div className="value">{formatQty(delivered)}</div>
                  <div className="hint">
                    an {trace.deliveries.length} Auftrag/Aufträge
                  </div>
                </div>
                <div className="kpi-tile">
                  <div className="label">Bewegungen</div>
                  <div className="value">{trace.movements.length}</div>
                </div>
              </div>
            </Card>

            <div className="grid two">
              <Card title="Aktueller Bestand" padded={false}>
                {trace.stock.length === 0 ? (
                  <Empty>Diese Charge liegt nicht mehr im Lager.</Empty>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Lager</th>
                        <th>Platz</th>
                        <th className="num">Menge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trace.stock.map((row) => (
                        <tr key={row.location_code}>
                          <td className="mono small">{row.warehouse_code}</td>
                          <td className="mono">{row.location_code}</td>
                          <td className="num">{formatQty(row.qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>

              <Card title="Ausgeliefert an" padded={false}>
                {trace.deliveries.length === 0 ? (
                  <Empty>Diese Charge hat das Lager noch nicht verlassen.</Empty>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Auftrag</th>
                        <th>Kunde</th>
                        <th>Versendet</th>
                        <th className="num">Menge</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trace.deliveries.map((row) => (
                        <tr key={row.order_number}>
                          <td className="mono">{row.order_number}</td>
                          <td>{row.customer_name ?? '—'}</td>
                          <td className="small">{formatDate(row.shipped_at)}</td>
                          <td className="num">{formatQty(row.qty)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Card>
            </div>

            <Card title="Lückenloser Weg" padded={false}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Zeitpunkt</th>
                      <th>Art</th>
                      <th className="num">Menge</th>
                      <th>Von</th>
                      <th>Nach</th>
                      <th>Beleg</th>
                      <th>Benutzer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trace.movements.map((movement) => (
                      <tr key={movement.id}>
                        <td className="small muted">{formatDateTime(movement.booked_at)}</td>
                        <td>
                          <ReasonBadge reason={movement.reason} />
                        </td>
                        <td className="num">{formatQty(movement.qty)}</td>
                        <td className="mono small">{movement.from_code ?? '—'}</td>
                        <td className="mono small">{movement.to_code ?? '—'}</td>
                        <td className="small mono">{movement.document_ref ?? '—'}</td>
                        <td className="small muted">{movement.user_name ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ))}
    </>
  );
}

type Dringlichkeit = 'abgelaufen' | 'kritisch' | 'bald';

interface Ablaufposten {
  lot_id: string;
  lot_code: string;
  best_before: string;
  tage: number;
  dringlichkeit: Dringlichkeit;
  sku: string;
  article_name: string;
  unit: string;
  owner_name: string;
  qty: number;
  plaetze: string;
}

interface Ablaufuebersicht {
  posten: Ablaufposten[];
  abgelaufen: number;
  kritisch: number;
  bald: number;
  mengeAbgelaufen: number;
}

const DRINGLICH: Record<Dringlichkeit, { kind: 'bad' | 'warn' | 'default'; text: string }> = {
  abgelaufen: { kind: 'bad', text: 'abgelaufen' },
  kritisch: { kind: 'warn', text: 'diese Woche' },
  bald: { kind: 'default', text: 'demn\u00e4chst' },
};

/**
 * Was demn\u00e4chst abl\u00e4uft.
 *
 * FEFO sorgt daf\u00fcr, dass die \u00e4lteste Ware zuerst rausgeht - nicht daf\u00fcr, dass
 * jemand davon erf\u00e4hrt, wenn ein Posten schlicht nicht abverkauft wird. Genau
 * das ist der Unterschied zwischen einer Abschreibung und einem Anruf beim
 * Auftraggeber.
 */
function Ablaufliste() {
  const [daten, setDaten] = useState<Ablaufuebersicht | null>(null);
  const [tage, setTage] = useState(30);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    let aktuell = true;
    api<Ablaufuebersicht>('/api/stock/expiry', { query: { tage: String(tage) } })
      .then((antwort) => aktuell && setDaten(antwort))
      .catch((err) => aktuell && setFehler(errorText(err)));
    return () => {
      aktuell = false;
    };
  }, [tage]);

  if (fehler) return <Notice kind="error">{fehler}</Notice>;
  if (!daten) return <Loading />;

  return (
    <Card
      title="Mindesthaltbarkeit"
      actions={
        <select value={tage} onChange={(e) => setTage(Number(e.target.value))}>
          <option value={7}>n\u00e4chste 7 Tage</option>
          <option value={30}>n\u00e4chste 30 Tage</option>
          <option value={90}>n\u00e4chste 90 Tage</option>
          <option value={365}>n\u00e4chstes Jahr</option>
        </select>
      }
      padded={false}
    >
      {daten.posten.length === 0 ? (
        <Empty>Nichts l\u00e4uft in diesem Zeitraum ab.</Empty>
      ) : (
        <>
          {daten.abgelaufen > 0 && (
            <div className="body" style={{ paddingBottom: 0 }}>
              <Notice kind="error">
                <strong>
                  {daten.abgelaufen} Charge{daten.abgelaufen === 1 ? '' : 'n'} mit{' '}
                  {formatQty(daten.mengeAbgelaufen)} Einheiten sind bereits \u00fcber dem Datum.
                </strong>{' '}
                Sie liegen noch auf regul\u00e4ren Pl\u00e4tzen und k\u00f6nnten kommissioniert werden.
              </Notice>
            </div>
          )}
          <table>
            <thead>
              <tr>
                <th>MHD</th>
                <th>Rest</th>
                <th>Charge</th>
                <th>Artikel</th>
                <th>Eigent\u00fcmer</th>
                <th className="num">Menge</th>
                <th>Liegt auf</th>
              </tr>
            </thead>
            <tbody>
              {daten.posten.map((posten) => (
                <tr key={posten.lot_id}>
                  <td className="small">{formatDate(posten.best_before)}</td>
                  <td>
                    <Badge kind={DRINGLICH[posten.dringlichkeit].kind}>
                      {posten.tage < 0
                        ? `${Math.abs(posten.tage)} Tage \u00fcber`
                        : `${posten.tage} Tage`}
                    </Badge>
                  </td>
                  <td className="mono small">{posten.lot_code}</td>
                  <td className="small">
                    <span className="mono">{posten.sku}</span> {posten.article_name}
                  </td>
                  <td className="small">{posten.owner_name}</td>
                  <td className="num">{formatQty(posten.qty, posten.unit)}</td>
                  <td className="small muted">{posten.plaetze}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="body small muted">
            Sperrlager und Gegenbuchungspl\u00e4tze bleiben aussen vor \u2014 dort liegt nichts, um das
            sich nicht schon jemand gek\u00fcmmert h\u00e4tte.
          </div>
        </>
      )}
    </Card>
  );
}
